import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { FormArray, FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { BaseComponent } from '../base-component/base.component';
import { PermittedAccess } from '../../../fleets/models/permitted-access.model';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { SectionState } from '../../enums/layout.enum';
import { ReceiverAPI } from '../../../receivers/services/receiver-api.service';
import { PermitsApiService } from '../../services/permits-api.service';
import { requiredKeys } from '@shared/validators/required-keys.validator';
import { preventDuplication } from '@shared/validators/prevent-duplication.validator';
import { UserApi } from '../../services';
import { CustomerServiceType } from '../../enums/shared-enums.enum';
import { Receiver } from '@features/receivers/models/receiver.model';

@Component({
  selector: 'app-permitted-access-list',
  templateUrl: './permitted-access-list.component.html',
  styleUrls: ['./permitted-access-list.component.scss']
})
export class PermittedAccessListComponent extends BaseComponent implements OnChanges {
  @Input() permitAccess: PermittedAccess[];
  @Input() customerId: number;
  @Input() driverId: number;
  @Input() truckId: number;
  @Input() savePermitAccess: (access: PermittedAccess[]) => Observable<any>;
  @ViewChild('permitAccessDialog') permitAccessDialog: ModalComponent;
  @ViewChild('editPermitAccessDialog') editPermitAccessDialog: ModalComponent;
  @Output() reload = new EventEmitter();
  // customerType = CustomerType;
  formArray = new FormArray([]);
  editFormControl = new FormControl(null, [
    requiredKeys(['receiver', 'expirationDate', 'label']),
    preventDuplication(this.formArray, this.formArray.controls.length, ['receiver', 'expirationDate'])
  ]);
  receivers: Receiver[];
  dataSource = new MatTableDataSource<PermittedAccess>();
  state = SectionState;
  pageIndex = 1;
  pageSize = 10;

  selectedPermitAccess: PermittedAccess;
  isCustomerTypeSAAS = this.userApiService.currentUser$.value?.customerType === CustomerServiceType.SAAS;

  public displayedColumns: string[] = [
    'label',
    'receiver',
    'expirationDate',
    'actions',
  ];

  constructor(

    private receiverApiService: ReceiverAPI,
    private permitsApiService: PermitsApiService,
    private userApiService: UserApi,
  ) {
    super();
  }

  ngOnChanges({ permitAccess }: SimpleChanges): void {
    if (permitAccess && permitAccess.currentValue !== permitAccess.previousValue) {
      this.onPageIndexChange(1);
    }
  }

  onPageIndexChange(index): void {
    const offset = (index > 1 ? index - 1 : 0) * this.pageSize;
    this.pageIndex = index;
    this.dataSource.data = (this.permitAccess || []).slice(offset, offset + this.pageSize);
  }

  openAddPermitDialog(): void {
    this.formArray = new FormArray([]);
    this.permitAccessDialog.showModal();
  }

  handleSavePermitAccess(): void {
    this.load(this.savePermitAccess([
      ...this.permitAccess.map(p => ({ ...p, receiverId: p.receiver?.id, expirationDate: new Date(p.expirationDate)} as any)),
      ...this.getPermitAccess(),
    ]))
      .subscribe(() => {
        this.showSuccessMessage('SuccessMessages.PermitAccessUpdatedSuccessfully');
        this.permitAccessDialog.closeModal();
        this.reload.emit();
      });
  }

  getPermitAccess(): PermittedAccess[] {
    const keys = ['receiver', 'label', 'expirationDate'];
    const itemKeysHaveValues = (item) => keys.every((key) => item[key]);
    return (this.formArray.value || [])
      .filter(itemKeysHaveValues)
      .map((item) => ({ label: item.label, receiverId: item.receiver.id, expirationDate: item.expirationDate }));
  }

  handleEdit(item: PermittedAccess): void {
    this.selectedPermitAccess = item;
    this.editFormControl.patchValue(this.selectedPermitAccess);
    if (!this.receivers?.length) {
      this.getReceivers().then(() => {
        this.editPermitAccessDialog.showModal();
      });
    } else {
      this.editPermitAccessDialog.showModal();
    }
  }

  handleDelete(item: PermittedAccess): void {
    this.selectedPermitAccess = item;
    const list = this.permitAccess.filter(i => i.id !== item.id).map(i => ({
      ...i,
      receiverId: i.receiver?.id,
    }));
    this.load(
      this.savePermitAccess(list),
    ).subscribe(() => {
      this.showSuccessMessage('SuccessMessages.PermitAccessDeletedSuccessfully');
      this.reload.emit();
    });
  }

  handleUpdatePermitAccess(): void {
    const access = {
      ...this.editFormControl.value,
      receiverId: this.editFormControl.value?.receiver.id,
      id: this.selectedPermitAccess.id,
    };
    this.load(
      this.permitsApiService.update(access, this.truckId, this.driverId),
    ).subscribe(() => {
      this.showSuccessMessage('SuccessMessages.PermitAccessUpdatedSuccessfully');
      this.editPermitAccessDialog.closeModal();
      this.selectedPermitAccess = null;
      this.reload.emit();
    });
  }

  getReceivers(): Promise<boolean> {
    return new Promise((resolve) => {
      this.load(
        this.receiverApiService.getAllReceivers(this.userApiService.currentUser$.value.parentId),
      ).subscribe((receivers) => {
        this.receivers = receivers;
        resolve(true);
      }, (err) => {
        resolve(true);
      });
    });
  }

  formArrayHasValidValue(): boolean {
    const value = this.formArray?.value || [];
    const keys = ['label', 'receiver', 'expirationDate'];
    let invalid = false;
    value.forEach(v => {
      keys.forEach(key => {
        if (v[key] === null || typeof v[key] === 'undefined') {
          invalid = true;
        }
      });
    });
    return !invalid;
  }
}
